// accessAuth.ts — shared clinic access code (gate in front of doctor login)
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import axios from 'axios'
import { API_URL, AppConstant } from '../../../../AppConstant'
import { APIStatus } from '../../../types'

// Backend route that validates the single code every doctor shares.
// Change this one constant if the API path differs.
const ACCESS_CODE_PATH = '/auth/clinic/access-code'

interface AccessAuthState {
  // Form state
  code: string

  // Verify async
  verifyAccessCodeStatus: APIStatus
  verifyAccessCodeError: string | null
}

const initialState: AccessAuthState = {
  code: '',

  verifyAccessCodeStatus: APIStatus.idle,
  verifyAccessCodeError: null,
}

// VERIFY ACCESS CODE
export const verifyAccessCodeAsync = createAsyncThunk<void, { code: string }, { rejectValue: string }>(
  'accessAuth/verifyCode',
  async ({ code }, thunkAPI) => {
    try {
      const response = await axios.post(
        `${API_URL}${ACCESS_CODE_PATH}`,
        { code },
        {
          headers: {
            'X-Platform-Id': process.env.REACT_APP_PLATFORM_ID,
          },
        },
      )
      // Backend may answer either with the plain success envelope or with
      // { data: { valid: boolean } } — treat an explicit `valid: false` as a failure.
      if (response && response.data.code === 'success' && response.data.data?.valid !== false) {
        localStorage.setItem(AppConstant.PORTAL_ACCESS, 'granted')
        return thunkAPI.fulfillWithValue(undefined)
      }
      return thunkAPI.rejectWithValue(response?.data?.message || 'Incorrect code')
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err.response?.data?.message || 'Incorrect code')
    }
  },
)

const accessAuthSlice = createSlice({
  name: 'accessAuth',
  initialState,
  reducers: {
    setAccessCode(state, action: PayloadAction<string>) {
      state.code = action.payload
      state.verifyAccessCodeError = null
    },
    resetAccessForm() {
      return { ...initialState }
    },
    clearAccess() {
      localStorage.removeItem(AppConstant.PORTAL_ACCESS)
      return { ...initialState }
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(verifyAccessCodeAsync.pending, (state) => {
        state.verifyAccessCodeStatus = APIStatus.pending
        state.verifyAccessCodeError = null
      })
      .addCase(verifyAccessCodeAsync.fulfilled, (state) => {
        state.verifyAccessCodeStatus = APIStatus.fulfilled
        state.code = ''
      })
      .addCase(verifyAccessCodeAsync.rejected, (state, action) => {
        state.verifyAccessCodeStatus = APIStatus.rejected
        state.verifyAccessCodeError = action.payload || 'Incorrect code'
      })
  },
})

export const { setAccessCode, resetAccessForm, clearAccess } = accessAuthSlice.actions

export default accessAuthSlice.reducer
